import { useState, useEffect, useRef, useCallback } from 'react';
import { parseGs1Barcode, Gs1ParsedData } from '../utils/gs1Parser';

interface UseHardwareScannerOptions {
  onScan?: (data: Gs1ParsedData) => void;
  enabled?: boolean;
  maxKeystrokeIntervalMs?: number; // Padrão 45ms (diferencia leitor físico HID de digitação humana)
  minBarcodeLength?: number;
}

export function useHardwareScanner({
  onScan,
  enabled = true,
  maxKeystrokeIntervalMs = 45,
  minBarcodeLength = 3
}: UseHardwareScannerOptions = {}) {
  const [lastScanned, setLastScanned] = useState<Gs1ParsedData | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const bufferRef = useRef<string>('');
  const lastKeyTimeRef = useRef<number>(0);
  const timestampsRef = useRef<number[]>([]);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const hasNativeBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  const handleBarcodeDetected = useCallback(
    (rawString: string) => {
      const parsed = parseGs1Barcode(rawString);
      setLastScanned(parsed);
      if (onScan) {
        onScan(parsed);
      }
    },
    [onScan]
  );

  // Escuta global de eventos de teclado (Leitores USB / Bluetooth em modo HID)
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignora digitação humana quando o foco estiver em inputs, textareas ou contenteditables normais
      const target = e.target as HTMLElement | null;
      if (target) {
        const isEditable =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable;

        const isDedicatedScannerInput =
          target.getAttribute('data-scanner-input') === 'true' ||
          target.getAttribute('data-barcode-input') === 'true';

        if (isEditable && !isDedicatedScannerInput) {
          bufferRef.current = '';
          timestampsRef.current = [];
          return;
        }
      }

      // Ignora teclas modificadoras e de controle puro
      if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab'].includes(e.key)) {
        return;
      }

      const now = Date.now();
      const interval = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      // Se o intervalo entre teclas for superior ao threshold (ex: > 45ms),
      // trata-se de digitação manual do operador: descarta qualquer buffer anterior
      if (bufferRef.current.length > 0 && interval > maxKeystrokeIntervalMs) {
        bufferRef.current = '';
        timestampsRef.current = [];
      }

      if (e.key === 'Enter') {
        const charCount = bufferRef.current.length;
        const times = timestampsRef.current;

        // Validação estrita de hardware scanner:
        // 1. Quantidade mínima de caracteres
        // 2. Velocidade média de rajada comprovadamente de scanner (< maxKeystrokeIntervalMs)
        const isHardwareBurst =
          charCount >= minBarcodeLength &&
          times.length >= minBarcodeLength &&
          (times[times.length - 1] - times[0]) / (times.length - 1) <= maxKeystrokeIntervalMs;

        if (isHardwareBurst) {
          const scannedText = bufferRef.current;
          bufferRef.current = '';
          timestampsRef.current = [];

          // Evita que o Enter do scanner submeta formulários aleatórios na tela
          e.preventDefault();
          e.stopPropagation();

          handleBarcodeDetected(scannedText);
        } else {
          // Digitação humana comum finalizada com Enter
          bufferRef.current = '';
          timestampsRef.current = [];
        }
        return;
      }

      // Concatena caracteres imprimíveis e ASCII de controle GS1
      if (e.key.length === 1) {
        bufferRef.current += e.key;
        timestampsRef.current.push(now);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [enabled, minBarcodeLength, maxKeystrokeIntervalMs, handleBarcodeDetected]);

  // Leitura por Câmera (BarcodeDetector API nativa)
  const startCameraScan = useCallback(
    async (videoElement: HTMLVideoElement): Promise<boolean> => {
      try {
        setCameraError(null);
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Acesso à câmera não é suportado neste navegador.');
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
        });

        cameraStreamRef.current = stream;
        videoElement.srcObject = stream;
        await videoElement.play();
        setIsCameraActive(true);

        if (hasNativeBarcodeDetector) {
          const barcodeDetector = new (window as any).BarcodeDetector({
            formats: ['qr_code', 'data_matrix', 'code_128', 'ean_13', 'itf']
          });

          const detectFrame = async () => {
            if (!videoElement || videoElement.paused || videoElement.ended) return;
            try {
              const barcodes = await barcodeDetector.detect(videoElement);
              if (barcodes && barcodes.length > 0) {
                const rawValue = barcodes[0].rawValue;
                if (rawValue) {
                  handleBarcodeDetected(rawValue);
                }
              }
            } catch (err) {
              // frame drop normal em detecção
            }
            animationFrameRef.current = requestAnimationFrame(detectFrame);
          };

          animationFrameRef.current = requestAnimationFrame(detectFrame);
        } else {
          setCameraError('Detecção nativa de código de barras não suportada neste browser. Utilize um leitor USB/Bluetooth.');
        }

        return true;
      } catch (err: any) {
        console.warn('[useHardwareScanner] Falha ao iniciar câmera:', err);
        setCameraError(err.message || 'Erro ao inicializar câmera.');
        setIsCameraActive(false);
        return false;
      }
    },
    [hasNativeBarcodeDetector, handleBarcodeDetected]
  );

  const stopCameraScan = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    setIsCameraActive(false);
  }, []);

  // Cleanup de câmera ao desmontar
  useEffect(() => {
    return () => {
      stopCameraScan();
    };
  }, [stopCameraScan]);

  return {
    lastScanned,
    isCameraActive,
    cameraError,
    hasNativeBarcodeDetector,
    startCameraScan,
    stopCameraScan,
    simulateScan: handleBarcodeDetected
  };
}
